import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ShoppingBag,
  Globe,
  Target,
  MessageSquare,
  Lightbulb,
  Sparkles,
  Calendar,
  Send,
  AlertCircle,
  CheckCircle,
  Share2,
  Clock,
  Link as LinkIcon
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface QuizAnswers {
  productName: string;
  productDescription: string;
  productUrl: string;
  platforms: string[];
  goal: string;
  promotionStyle: string;
}

const ProductCampaignQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    productName: '',
    productDescription: '',
    productUrl: '',
    platforms: [],
    goal: '',
    promotionStyle: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    posts: Array<{
      content: string;
      platform: string;
      cta: string;
      scheduledFor?: string;
    }>;
    angles: string[];
    hooks: string[];
  } | null>(null);

  const steps = [
    {
      title: "Tell us about your product",
      description: "Enter the details of what you're promoting",
      fields: [
        {
          id: 'productName',
          label: 'Product Name',
          type: 'text',
          placeholder: 'Enter your product name'
        },
        {
          id: 'productDescription',
          label: 'Product Description',
          type: 'textarea',
          placeholder: 'Describe your product and its key benefits'
        },
        {
          id: 'productUrl',
          label: 'Product URL (optional)',
          type: 'url',
          placeholder: 'https://...'
        }
      ]
    },
    {
      title: "Where do you want to promote?",
      description: "Select the platforms for your campaign",
      options: [
        { id: 'twitter', label: 'Twitter', icon: 'twitter' },
        { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
        { id: 'instagram', label: 'Instagram', icon: 'instagram' },
        { id: 'facebook', label: 'Facebook', icon: 'facebook' },
        { id: 'youtube', label: 'YouTube', icon: 'youtube' }
      ],
      multiSelect: true
    },
    {
      title: "What's your goal?",
      description: "Choose your primary objective",
      options: [
        { id: 'traffic', label: 'Drive Website Traffic', icon: Globe },
        { id: 'leads', label: 'Generate Leads/DMs', icon: MessageSquare },
        { id: 'sales', label: 'Direct Sales', icon: ShoppingBag },
        { id: 'awareness', label: 'Product Awareness', icon: Target }
      ]
    },
    {
      title: "Choose your promotion style",
      description: "Select how you want to present your product",
      options: [
        { id: 'educational', label: 'Educational + Value-Led', icon: Lightbulb },
        { id: 'direct', label: 'Direct Pitch', icon: ShoppingBag },
        { id: 'story', label: 'Storytelling', icon: MessageSquare },
        { id: 'viral', label: 'Viral/Meme Format', icon: Sparkles }
      ]
    }
  ];

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save campaign data
      const { data: campaign, error: campaignError } = await supabase
        .from('product_campaigns')
        .insert({
          user_id: user.id,
          product_name: answers.productName,
          product_description: answers.productDescription,
          product_url: answers.productUrl,
          platforms: answers.platforms,
          goal: answers.goal,
          promotion_style: answers.promotionStyle
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Generate content using OpenAI
      const prompt = `Create a product promotion campaign for ${answers.productName}.

Product details:
${answers.productDescription}
${answers.productUrl ? `URL: ${answers.productUrl}` : ''}

Platforms: ${answers.platforms.join(', ')}
Goal: ${answers.goal}
Style: ${answers.promotionStyle}

Format your response as JSON with the following structure:
{
  "posts": [
    {
      "content": "post content",
      "platform": "platform name",
      "cta": "call to action",
      "scheduledFor": "suggested posting time"
    }
  ],
  "angles": ["marketing angle 1", "marketing angle 2", "marketing angle 3"],
  "hooks": ["attention-grabbing hook 1", "attention-grabbing hook 2", "attention-grabbing hook 3"]
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            setGeneratedContent(data);
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

      // Save generated content
      if (generatedContent) {
        const contentPromises = generatedContent.posts.map(post => 
          supabase
            .from('product_campaign_content')
            .insert({
              campaign_id: campaign.id,
              content: post.content,
              platform: post.platform,
              post_type: answers.promotionStyle,
              cta: post.cta,
              scheduled_for: post.scheduledFor
            })
        );

        await Promise.all(contentPromises);
      }

    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {step.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {step.description}
          </p>
        </div>

        {'fields' in step ? (
          <div className="space-y-4">
            {step.fields.map(field => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={answers[field.id as keyof QuizAnswers] as string}
                    onChange={(e) => setAnswers(prev => ({
                      ...prev,
                      [field.id]: e.target.value
                    }))}
                    placeholder={field.placeholder}
                    rows={4}
                    className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  />
                ) : (
                  <input
                    type={field.type}
                    value={answers[field.id as keyof QuizAnswers] as string}
                    onChange={(e) => setAnswers(prev => ({
                      ...prev,
                      [field.id]: e.target.value
                    }))}
                    placeholder={field.placeholder}
                    className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {step.options.map((option) => {
              const Icon = typeof option.icon === 'string' ? Globe : option.icon;
              const field = 
                currentStep === 1 ? 'platforms' :
                currentStep === 2 ? 'goal' : 'promotionStyle';
              
              const isSelected = step.multiSelect
                ? answers[field] instanceof Array && answers[field].includes(option.id)
                : answers[field] === option.id;

              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAnswers(prev => ({
                    ...prev,
                    [field]: step.multiSelect
                      ? (prev[field] instanceof Array
                          ? isSelected
                            ? prev[field].filter(id => id !== option.id)
                            : [...prev[field], option.id]
                          : [option.id])
                      : option.id
                  }))}
                  className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    isSelected
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="ml-3 font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Product Campaign
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here's your personalized promotion strategy for {answers.productName}
          </p>
        </div>

        {/* Marketing Angles */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Target className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              Marketing Angles
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {generatedContent.angles.map((angle, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <p className="text-gray-700 dark:text-gray-300">{angle}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Attention Hooks */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Lightbulb className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">
              Attention-Grabbing Hooks
            </h3>
          </div>
          <div className="space-y-3">
            {generatedContent.hooks.map((hook, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <p className="text-gray-700 dark:text-gray-300">{hook}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Generated Posts */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Campaign Posts
          </h3>
          {generatedContent.posts.map((post, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-sm">
                      {post.platform}
                    </span>
                    {post.scheduledFor && (
                      <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.scheduledFor}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 dark:text-white">{post.content}</p>
                  <div className="mt-4 flex items-center space-x-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <LinkIcon className="h-4 w-4 mr-1" />
                      CTA: {post.cta}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setAnswers({
                productName: '',
                productDescription: '',
                productUrl: '',
                platforms: [],
                goal: '',
                promotionStyle: ''
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule All
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Generating your campaign...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / (steps.length - 1)) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderCurrentStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={
                currentStep === 0 ? !answers.productName || !answers.productDescription :
                currentStep === 1 ? answers.platforms.length === 0 :
                currentStep === 2 ? !answers.goal :
                !answers.promotionStyle
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                (currentStep === 0 && answers.productName && answers.productDescription) ||
                (currentStep === 1 && answers.platforms.length > 0) ||
                (currentStep === 2 && answers.goal) ||
                (currentStep === 3 && answers.promotionStyle)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Generate Campaign
                  <Send className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCampaignQuiz;